import { Box } from "@mui/material";
import Head from "next/head";
import { useEffect } from "react";
import Breadcrumbs from "../../../../src/components/common/Breadcrumbs";
import { newVehicles, vehicles } from "../../../../src/constants/vehicleData";
import { MemoizedFooter } from "../../../../src/layouts/footer";
import { MemoizedHeader } from "../../../../src/layouts/header";
import BookVehicleDrawer from "../../../../src/modules/Home/Drawer/bookVehicleDrawer";
import { MemoizedLeadCapture } from "../../../../src/pages/LeadCapture";
import { sendAnalytics } from "../../../../src/utils/analyticsCommon";
import {
  generateProductSchema,
  createOffer,
  createBrand,
  createPropertyValue,
} from "../../../../src/utils/ProductSchemaGenerator.js";
import {
  generateFAQPageSchema,
  convertFAQsToQuestions,
} from "../../../../src/utils/FAQPageSchemaGenerator.js";
import { vehicleFAQs } from "../../../../src/constants/brandFaqs";
import { imagesWEBP } from "../../../../src/constants/webpImages";
import {
  TITLE,
  META_CONTENT,
  VEHICLE_LINK,
  BREADCRUMB_ITEMS,
  BREADCRUMB_SCHEMA,
} from "../../../../src/constants/electricCommericalVehicle/OSM/ragePlusFrost.js";

export default function VehicleModelDetails({
  brand,
  vehicleModel,
  vehicleDataLocal,
  productSchema,
  faqPageSchema,
}) {
  useEffect(() => {
    sendAnalytics(
      "screen_viewed",
      { screen_name: `electric_commercial_vehicle_${brand}_${vehicleModel}` },
      "en"
    );
  }, [brand, vehicleModel]);

  return (
    <>
      <Head>
        <title>{TITLE}</title>
        {META_CONTENT.map((meta, index) => (
          <meta
            key={index}
            {...(meta.name ? { name: meta.name } : { property: meta.property })}
            content={meta.content}
          />
        ))}
        <link rel="canonical" href={VEHICLE_LINK} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(BREADCRUMB_SCHEMA),
          }}
          defer
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(productSchema),
          }}
          defer
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqPageSchema),
          }}
          defer
        />
      </Head>
      <Box>
      <MemoizedHeader />
      {/* Main component */}

      <BookVehicleDrawer />
      <MemoizedLeadCapture vehicleDataLocal={vehicleDataLocal} />
      <Box
        sx={{
          width: { xs: "100%", md: "84%" },
          margin: "20px auto",
          padding: { xs: "0 12px", md: "0" },
        }}
      >
        <Breadcrumbs items={BREADCRUMB_ITEMS} />
      </Box>
      <MemoizedFooter />
      </Box>
    </>
  );
}

export const getStaticProps = (context) => {
  let vehicleData = [...vehicles, ...newVehicles];

  const brand = "OSM";
  const vehicleModel = "Rage-+-Frost";

  let vehicleDataLocal = vehicleData?.filter(function (vehicle) {
    return vehicle?.vehicle_path_id === `${vehicleModel}`;
  });

  // Constants are already imported at the top of the file

  // Generate Product Schema
  const vehicleDataItem = vehicleDataLocal?.[0];
  const productImages = vehicleDataItem?.images_287 || [
    imagesWEBP?.osmRagePlusImage1,
    imagesWEBP?.osmRagePlusImage2,
    imagesWEBP?.osmRagePlusImage3,
    imagesWEBP?.osmRagePlusImage4,
  ].filter(Boolean);

  // Get price range from vehicle data
  const getPriceRange = () => {
    if (!vehicleDataItem?.prices) return { lowPrice: 400000, highPrice: 450000 };
    const allPrices = [];
    Object.values(vehicleDataItem.prices).forEach((statePrices) => {
      Object.values(statePrices).forEach((variant) => {
        if (variant.price) {
          allPrices.push(variant.price * 100000); // Convert lakhs to rupees
        }
      });
    });
    return {
      lowPrice: Math.min(...allPrices),
      highPrice: Math.max(...allPrices),
    };
  };

  const priceRange = getPriceRange();

  const productSchema = generateProductSchema({
    name: "OSM Rage+ Frost",
    description: META_CONTENT[0]?.content || "OSM Rage+ Frost electric commercial vehicle",
    image: productImages,
    url: VEHICLE_LINK,
    brand: createBrand({
      name: "OSM",
      url: "https://www.turno.club/electric-commercial-vehicles/OSM",
    }),
    category: "Electric Commercial Vehicle",
    model: "Rage+ Frost",
    manufacturer: {
      "@type": "Organization",
      name: "OSM Electric",
    },
    offers: createOffer({
      offerCount: 3,
      lowPrice: priceRange.lowPrice,
      highPrice: priceRange.highPrice,
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      url: VEHICLE_LINK,
      seller: {
        "@type": "Organization",
        name: "Turno",
        url: "https://www.turno.club/",
      },
    }),
    additionalProperty: [
      vehicleDataItem?.load
        ? createPropertyValue({ name: "Load Capacity", value: vehicleDataItem.load })
        : null,
      vehicleDataItem?.range
        ? createPropertyValue({ name: "Range", value: vehicleDataItem.range })
        : null,
      vehicleDataItem?.batteryCapacity
        ? createPropertyValue({
            name: "Battery Capacity",
            value: vehicleDataItem.batteryCapacity,
          })
        : null,
      vehicleDataItem?.batteryVoltage
        ? createPropertyValue({
            name: "Battery Voltage",
            value: vehicleDataItem.batteryVoltage,
          })
        : null,
    ].filter(Boolean),
    keywords: META_CONTENT[1]?.content || "OSM Rage+ Frost, electric vehicle",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": VEHICLE_LINK,
    },
    itemCondition: "https://schema.org/NewCondition",
    isFamilyFriendly: true,
    slogan: "Electric three-wheeler for cargo transportation",
  });

  // Generate FAQPage Schema - using general OSM FAQs if available
  const faqQuestions = convertFAQsToQuestions(vehicleFAQs?.osm || [], {
    url: VEHICLE_LINK,
    dateCreated: new Date().toISOString(),
    author: {
      "@type": "Organization",
      name: "Turno",
      url: "https://www.turno.club/",
    },
  });

  const faqPageSchema = generateFAQPageSchema({
    mainEntity: faqQuestions,
    name: "FAQs on OSM Rage+ Frost",
    description: `Frequently asked questions about OSM Rage+ Frost electric commercial vehicle. ${META_CONTENT[0]?.content || ""}`,
    url: VEHICLE_LINK,
    headline: "FAQs on OSM Rage+ Frost",
    keywords: META_CONTENT[1]?.content || "OSM Rage+ Frost, FAQ, electric vehicle",
    inLanguage: "en",
    publisher: {
      "@type": "Organization",
      name: "Turno",
      url: "https://www.turno.club/",
      logo: {
        "@type": "ImageObject",
        url: "https://www.turno.club/logoIcon.png",
      },
    },
    datePublished: new Date().toISOString(),
    dateModified: new Date().toISOString(),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": VEHICLE_LINK,
    },
    breadcrumb: BREADCRUMB_SCHEMA,
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: productImages[0] || "https://www.turno.club/logoIcon.png",
    },
    isFamilyFriendly: true,
    isAccessibleForFree: true,
    speakable: {
      "@type": "SpeakableSpecification",
      xPath: [
        "/html/head/title",
        "/html/head/meta[@name='description']/@content",
      ],
    },
  });

  return {
    props: { 
      brand, 
      vehicleModel, 
      vehicleDataLocal,
      productSchema,
      faqPageSchema,
    },
  };
};